import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import {
  Text,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Badge,
  Field,
  Textarea,
  Dropdown,
  Option,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { PersonRegular, EditRegular, DeleteRegular } from '@fluentui/react-icons';
import {
  useClientNotes,
  useCreateClientNote,
  useDeleteClientNote,
  useUpdateClientNote,
  CLIENT_NOTE_KINDS,
} from '../../services/stateTaxClientsApi';
import type { ClientNote } from '../../types';
import { appLocale } from '../../utils/appLocale';

/**
 * Cosmos-backed staff timeline. Distinct from the persistent client description
 * (`client.notes` mirrors the Dataverse `cr8f7_notes` field). New notes are
 * stamped with the active MSAL account name when available; edit/delete
 * actions surface inline on each row.
 */
const useStyles = makeStyles({
  card: { width: '100%' },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  heading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  composer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
  },
  composerRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  composerKind: {
    minWidth: '160px',
    marginRight: 'auto',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  noteItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: tokens.spacingVerticalS,
    borderLeft: `3px solid ${tokens.colorBrandStroke2}`,
    paddingLeft: tokens.spacingHorizontalM,
  },
  noteMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexWrap: 'wrap',
  },
  noteAuthor: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  noteBody: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  // Edit/Delete affordances in the meta row. The first carries auto-margin so
  // it pushes both buttons to the right edge; the second tiles next to it.
  editButton: {
    marginLeft: 'auto',
    minWidth: '24px',
    width: '24px',
    height: '24px',
    padding: 0,
  },
  deleteButton: {
    minWidth: '24px',
    width: '24px',
    height: '24px',
    padding: 0,
  },
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  editActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
});

function formatNoteTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(appLocale(), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function NotesCard({ stateTaxClientId }: { stateTaxClientId: string }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { data, isLoading, isError } = useClientNotes(stateTaxClientId);
  const notes = data ?? [];

  return (
    <div className={`card ${styles.card}`}>
      <div className={styles.header}>
        <Text className={styles.heading}>
          {t('clientDetailPage.notes.title')}
        </Text>
        <Text className={styles.subtitle}>
          {t('clientDetailPage.notes.subtitle')}
        </Text>
      </div>

      <NoteComposer stateTaxClientId={stateTaxClientId} />

      {isLoading && <Spinner size="tiny" label={t('clientDetailPage.notes.loading')} />}

      {isError && (
        <MessageBar intent="warning">
          <MessageBarBody>
            {t('clientDetailPage.notes.unavailable')}
          </MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !isError && notes.length === 0 && (
        <Text className={styles.emptyHint}>
          {t('clientDetailPage.notes.empty')}
        </Text>
      )}

      {!isLoading && !isError && notes.length > 0 && (
        <div className={styles.timeline}>
          {notes.map((n) => (
            <NoteItem key={n.id} note={n} stateTaxClientId={stateTaxClientId} />
          ))}
        </div>
      )}
    </div>
  );
}

function NoteItem({ note, stateTaxClientId }: { note: ClientNote; stateTaxClientId: string }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const author = note.createdByName?.trim() || t('clientDetailPage.notes.unknownAuthor');
  const deleteNote = useDeleteClientNote();
  const updateNote = useUpdateClientNote();

  // Inline edit state. Idle = render the note as text + Edit/Delete affordances.
  // Editing = render a textarea pre-filled with the current text + Save/Cancel.
  // Save commits via the PATCH endpoint and exits the editing state on success;
  // server-side preserves createdAt + createdBy* so the timeline stays honest.
  const [editing, setEditing] = useState(false);
  const [draftText, setDraftText] = useState(note.text);

  function startEdit() {
    setDraftText(note.text);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setDraftText(note.text);
  }

  function saveEdit() {
    const trimmed = draftText.trim();
    if (trimmed.length === 0 || trimmed === note.text) {
      cancelEdit();
      return;
    }
    updateNote.mutate(
      { stateTaxClientId, noteId: note.id, payload: { text: trimmed } },
      { onSuccess: () => setEditing(false) },
    );
  }

  function handleDelete() {
    // Soft confirmation — staff occasionally hits delete by accident, and this is
    // a hard delete with no undo. The browser confirm is intrusive but lightweight
    // and matches what other audit-aware delete spots in the app do.
    const ok = window.confirm(t('clientDetailPage.notes.deleteConfirm'));
    if (!ok) return;
    deleteNote.mutate({ stateTaxClientId, noteId: note.id });
  }

  return (
    <div className={styles.noteItem}>
      <div className={styles.noteMeta}>
        <span className={styles.noteAuthor}>
          <PersonRegular fontSize={14} />
          {author}
        </span>
        <span>·</span>
        <span>{formatNoteTimestamp(note.createdAt)}</span>
        {note.kind && note.kind !== 'general' && (
          <Badge appearance="outline" size="small">
            {t(`clientDetailPage.notes.kinds.${note.kind}`, { defaultValue: note.kind })}
          </Badge>
        )}
        {!editing && (
          <>
            <Button
              appearance="subtle"
              size="small"
              icon={<EditRegular />}
              onClick={startEdit}
              disabled={deleteNote.isPending || updateNote.isPending}
              className={styles.editButton}
              title={t('clientDetailPage.notes.editTitle')}
              aria-label={t('clientDetailPage.notes.editAria', { author })}
            />
            <Button
              appearance="subtle"
              size="small"
              icon={<DeleteRegular />}
              onClick={handleDelete}
              disabled={deleteNote.isPending || updateNote.isPending}
              className={styles.deleteButton}
              title={t('clientDetailPage.notes.deleteTitle')}
              aria-label={t('clientDetailPage.notes.deleteAria', { author })}
            />
          </>
        )}
      </div>
      {editing ? (
        <div className={styles.editForm}>
          <Textarea
            value={draftText}
            onChange={(_, data) => setDraftText(data.value)}
            rows={3}
            disabled={updateNote.isPending}
          />
          <div className={styles.editActions}>
            <Button
              appearance="secondary"
              size="small"
              onClick={cancelEdit}
              disabled={updateNote.isPending}
            >
              {t('clientDetailPage.notes.cancelEdit')}
            </Button>
            <Button
              appearance="primary"
              size="small"
              onClick={saveEdit}
              disabled={updateNote.isPending || draftText.trim().length === 0}
            >
              {updateNote.isPending
                ? t('clientDetailPage.notes.saving')
                : t('clientDetailPage.notes.saveEdit')}
            </Button>
          </div>
        </div>
      ) : (
        <Text className={styles.noteBody}>{note.text}</Text>
      )}
    </div>
  );
}

function NoteComposer({ stateTaxClientId }: { stateTaxClientId: string }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { accounts } = useMsal();
  const activeAccount = accounts[0];

  const [text, setText] = useState('');
  const [kind, setKind] = useState<string>('general');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const createNote = useCreateClientNote();

  function handleSubmit() {
    setErrorMsg(null);
    const trimmed = text.trim();
    if (trimmed.length === 0) return;

    createNote.mutate(
      {
        stateTaxClientId,
        payload: {
          text: trimmed,
          kind,
          createdByName: activeAccount?.name ?? activeAccount?.username,
          createdById: activeAccount?.localAccountId,
        },
      },
      {
        onSuccess: () => {
          setText('');
          setKind('general');
        },
        onError: (err) => {
          setErrorMsg(err instanceof Error ? err.message : t('clientDetailPage.notes.saveFailed'));
        },
      },
    );
  }

  const disabled = createNote.isPending || text.trim().length === 0;

  return (
    <div className={styles.composer}>
      <Field>
        <Textarea
          placeholder={t('clientDetailPage.notes.placeholder')}
          value={text}
          onChange={(_, data) => setText(data.value)}
          rows={3}
          disabled={createNote.isPending}
        />
      </Field>
      <div className={styles.composerRow}>
        <div className={styles.composerKind}>
          <Dropdown
            value={t(`clientDetailPage.notes.kinds.${kind}`)}
            selectedOptions={[kind]}
            onOptionSelect={(_, data) => setKind(data.optionValue ?? 'general')}
            disabled={createNote.isPending}
          >
            {CLIENT_NOTE_KINDS.map((k) => (
              <Option key={k} value={k}>
                {t(`clientDetailPage.notes.kinds.${k}`)}
              </Option>
            ))}
          </Dropdown>
        </div>
        <Button
          appearance="primary"
          onClick={handleSubmit}
          disabled={disabled}
        >
          {createNote.isPending
            ? t('clientDetailPage.notes.saving')
            : t('clientDetailPage.notes.addNote')}
        </Button>
      </div>
      {errorMsg && (
        <MessageBar intent="error">
          <MessageBarBody>{errorMsg}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
